import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, LinkAuthenticationElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useNotifications } from './Notifications';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'YOUR_STRIPE_PUBLISHABLE_KEY');

// Credit packages - one table, used by both the picker and the purchase button.
const CREDIT_PACKAGES = [
  { credits: 10, price: 3, label: '10 Credits', savings: null },
  { credits: 20, price: 5, label: '20 Credits', savings: '$1 saved' },
  { credits: 50, price: 10, label: '50 Credits', savings: '$5 saved', popular: true },
  { credits: 120, price: 20, label: '120 Credits', savings: '$16 saved' },
  { credits: 360, price: 50, label: '360 Credits', savings: '$58 saved' },
  { credits: 800, price: 100, label: '800 Credits', savings: '$140 saved' },
];

// Payment form component that uses Stripe Elements
const PaymentForm = ({ selectedPackage, onSuccess, user, getAuthHeaders }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [email, setEmail] = useState(user?.email || '');
  const [linkAuthenticated, setLinkAuthenticated] = useState(false);
  const { notifyError, notifyWarning, notifySuccess, notifyApiError } = useNotifications();
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 
                    (process.env.NODE_ENV === 'production' ? 'https://api.aitools.syncode.it' : 'http://localhost:5000');

  const getSelectedPackage = () => {
    return CREDIT_PACKAGES.find(pkg => pkg.price === selectedPackage);
  };

  // Handle payment element changes for validation
  const handlePaymentChange = (event) => {
    setPaymentError(event.error ? event.error.message : null);
    setPaymentReady(event.complete);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);

    if (!stripe || !elements) {
      notifyWarning('The payment form is still loading. Please try again in a moment.');
      setIsProcessing(false);
      return;
    }

    // Card element is handled automatically by confirmPayment

    try {
      // Create payment intent
      const headers = await getAuthHeaders();
      const response = await axios.post(`${backendUrl}/api/create-payment-intent`, {
        amount: selectedPackage,
      }, { headers });

      // Submit the elements before confirming payment
      console.log('Submitting elements...');
      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error('Elements submit failed:', submitError);
        notifyWarning(submitError.message || 'Please check your payment details and try again.');
        return;
      }

      // Confirm payment using the more flexible confirmPayment method (supports Link)
      console.log('Confirming payment with Stripe...', response.data.clientSecret);
      const result = await stripe.confirmPayment({
        elements,
        clientSecret: response.data.clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/payment-complete`,
          payment_method_data: {
            billing_details: {
              name: user?.displayName || 'Customer',
              email: email || user?.email,
            },
          },
        },
        redirect: 'if_required', // Only redirect if required by the payment method
      });

      console.log('Stripe confirmPayment result:', result);

      if (result.error) {
        console.error('Payment failed:', result.error);
        notifyError(result.error.message || 'The payment could not be completed. Please try again.');
      } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        // Confirm payment on backend
        console.log('Payment succeeded, confirming with backend...');
        const confirmHeaders = await getAuthHeaders();
        await axios.post(`${backendUrl}/api/confirm-payment`, {
          paymentIntentId: result.paymentIntent.id,
        }, { headers: confirmHeaders });
        
        onSuccess();
        notifySuccess('Payment complete. Your credits have been added.');
      } else {
        console.error('Unexpected payment result:', result);
        notifyError('Your payment is still processing. If credits do not appear shortly, contact support with reference ' +
          (result.paymentIntent?.id || 'unknown') + '.', { duration: 0 });
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      notifyApiError(error, 'We could not process that payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
      {/* Link Authentication Element */}
      <div style={{ 
        padding: '15px', 
        border: '1px solid #ccc', 
        borderRadius: '5px', 
        marginBottom: '15px',
        backgroundColor: '#f9f9f9'
      }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '10px', 
          fontWeight: 'bold',
          color: '#333'
        }}>
          Email (for Link and receipts)
        </label>
        <LinkAuthenticationElement 
          onChange={(event) => {
            setEmail(event.value.email);
            if (event.complete) {
              // User is authenticated with Link
              setLinkAuthenticated(true);
            }
          }}
          options={{
            defaultValues: {
              email: email,
            },
          }}
        />
        <div style={{ 
          fontSize: '12px', 
          color: linkAuthenticated ? '#27ae60' : '#666', 
          marginTop: '5px',
          lineHeight: '1.4'
        }}>
          {linkAuthenticated ? '✅ Link authenticated - ready for one-click payment!' : '💫 Link saves your info securely for faster future payments'}
        </div>
      </div>

      {/* Payment Element - handles both cards and Link */}
      <div style={{ 
        padding: '15px', 
        border: paymentError ? '1px solid #e74c3c' : paymentReady ? '1px solid #27ae60' : '1px solid #ccc', 
        borderRadius: '5px', 
        marginBottom: '20px',
        backgroundColor: paymentError ? '#fdf2f2' : paymentReady ? '#f0f9f4' : '#f9f9f9',
        transition: 'all 0.2s ease'
      }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '10px', 
          fontWeight: 'bold',
          color: '#333'
        }}>
          Payment Details
        </label>
        <PaymentElement 
          onChange={handlePaymentChange}
          options={{
            layout: 'tabs'
          }}
        />
        {paymentError && (
          <div style={{ 
            color: '#e74c3c', 
            fontSize: '14px', 
            marginTop: '8px',
            fontWeight: '500'
          }}>
            {paymentError}
          </div>
        )}
      </div>
      
      <button
        type="submit"
        className="btn btn-primary"
        disabled={isProcessing || !stripe || !paymentReady || paymentError}
        style={{ 
          width: '100%',
          opacity: (!paymentReady || paymentError) ? 0.6 : 1,
          cursor: (!paymentReady || paymentError) ? 'not-allowed' : 'pointer'
        }}
      >
        {isProcessing ? 'Processing...' : 
          !paymentReady ? 'Enter payment details to continue' :
          `Purchase ${getSelectedPackage().credits} Credits for $${selectedPackage}`
        }
      </button>
    </form>
  );
};

const PaymentModal = ({ onSuccess, onClose, user, getAuthHeaders }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(10); // Default to most popular package
  const { notifySuccess, notifyApiError } = useNotifications();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 
                    (process.env.NODE_ENV === 'production' ? 'https://api.aitools.syncode.it' : 'http://localhost:5000');

  const isDevelopment = process.env.NODE_ENV === 'development';

  const getSelectedPackage = () => {
    return CREDIT_PACKAGES.find(pkg => pkg.price === selectedPackage);
  };

  const handleFakePayment = async () => {
    setIsProcessing(true);
    
    try {
      const selectedPkg = getSelectedPackage();
      const headers = await getAuthHeaders();
      
      // Simulate payment success directly
      await axios.post(`${backendUrl}/api/dev-add-credits`, {
        credits: selectedPkg.credits
      }, { headers });
      
      onSuccess();
      notifySuccess(`Development mode: ${selectedPkg.credits} credits added for free.`);
    } catch (error) {
      console.error('Error in fake payment:', error);
      notifyApiError(error, 'Could not add credits. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="payment-modal">
      <div className="payment-modal-content">
        <button className="close-button" onClick={onClose}>&times;</button>
        
        <h2>Purchase Credits</h2>
        <p>You need credits to generate AI images. Each credit = 1 high-quality 1024×1024 image.</p>
        <p style={{fontSize: '14px', color: '#666', margin: '0 0 15px 0'}}>💡 Great value pricing: ~$0.04-0.30 per credit vs $0.039 API cost</p>
        
        <div className="package-selection">
          {CREDIT_PACKAGES.map((pkg) => (
            <label key={pkg.credits} className={`package-option ${selectedPackage === pkg.price ? 'selected' : ''} ${pkg.popular ? 'popular' : ''}`}>
              <input
                type="radio"
                value={pkg.price}
                checked={selectedPackage === pkg.price}
                onChange={(e) => setSelectedPackage(parseInt(e.target.value))}
              />
              {pkg.popular && <div className="popular-badge">Most Popular</div>}
              <div className="package-details">
                <div className="package-main">
                  <strong>{pkg.label}</strong>
                  <span className="package-price">${pkg.price}</span>
                </div>
                {pkg.savings && (
                  <div className="package-savings">{pkg.savings}</div>
                )}
                <div className="package-rate">
                  ${(pkg.price / pkg.credits).toFixed(3)} per credit
                </div>
              </div>
            </label>
          ))}
        </div>
        
        <div className="payment-actions">
          {isDevelopment ? (
            <button
              className="btn btn-primary"
              onClick={handleFakePayment}
              disabled={isProcessing}
              style={{ width: '100%', marginTop: '20px' }}
            >
              {isProcessing ? 'Processing...' : 
                `Get ${getSelectedPackage().credits} Credits FREE (Dev Mode)`
              }
            </button>
          ) : (
            <Elements 
              stripe={stripePromise}
              options={{
                mode: 'payment',
                amount: selectedPackage * 100, // Convert to cents
                currency: 'usd',
                appearance: {
                  theme: 'stripe',
                },
              }}
            >
              <PaymentForm 
                selectedPackage={selectedPackage}
                onSuccess={onSuccess}
                user={user}
                getAuthHeaders={getAuthHeaders}
              />
            </Elements>
          )}
        </div>
        
        {isDevelopment ? (
          <div style={{ fontSize: '12px', color: '#28a745', marginTop: '20px', textAlign: 'center' }}>
            <p>🔧 <strong>Development Mode</strong></p>
            <p>Credits will be added for free - no payment required!</p>
          </div>
        ) : (
          <p style={{ fontSize: '12px', color: '#666', marginTop: '20px' }}>
            Secure payment powered by Stripe
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;